/**
 * Utility functions for transforming form data between React Hook Form format and regulator.js format
 */

// List of checkbox fields that should be "checked" when true
const CHECKBOX_FIELDS = new Set([
  'voltConnected', 'voltConnected2',
  'tempConnected', 'tempConnected2',
  'shuntInstalled', 'shuntInstalled2',
  'statorInstalled', 'statorInstalled2',
  'altShuntInstalled', 'altShuntInstalled2',
  'rpmReduceField', 'rpmReduceField2',
  'boostMode', 'boostMode2',
  'postCvSwitch', 'postCvSwitch2',
  'chargeCutoffInstalled', 'chargeCutoffInstalled2',
  'canbusRvc', 'canbusMarine', // 'canbusVictron' removed - deprecated after firmware 20250206
  'canbusRequired', 'canbusRequired2',
  'autoUpdateSwitch',
  'enableBmsProtocols', // BMS protocols toggle
  'ignitionRequired2', // Ignition requirement for alt2
])

// List of UI state fields that should be excluded from exports (will be reconstructed on load)
const EXCLUDED_FROM_EXPORT_FIELDS = new Set([
  'numBatteriesManaged', // UI state - reconstructed from Battery 2 field presence
  'batteryType',        // UI state - battery dropdown selection
  'batteryType2',       // UI state - battery dropdown selection for second bank
  'type',               // Battery metadata field - duplicate of batteryType
  'type2',              // Battery metadata field - duplicate of batteryType2
  'brand',              // Battery metadata field - not needed in export
  'brand2',             // Battery metadata field - not needed in export
  'model',              // Battery metadata field - not needed in export
  'model2',             // Battery metadata field - not needed in export
  'batteryInstanceA',   // Redundant field - base batteryInstance represents instance A
  'batteryInstanceA2',   // Redundant field - base batteryInstance2 represents instance A
])

// Transform value based on field type and direction
const transformValue = (key, value, direction, options = {}) => {
  const isCheckbox = CHECKBOX_FIELDS.has(key)
  
  // Early return for non-checkbox fields
  if (!isCheckbox) {
    return direction === 'toRegulator' && options.convertEmptyToNull && value === '' 
      ? 'null' 
      : value
  }
  
  // Checkbox field transformations
  return direction === 'toRegulator'
    ? (value === true ? 'checked' : 'null')
    : (value === 'checked' ? true : false) // Always convert non-"checked" to false for checkboxes
}

/**
 * Transform form data from React Hook Form format to regulator.js format
 * Converts boolean checkbox values to "checked" strings and handles empty values
 * 
 * @param {Object} formData - The form data from React Hook Form
 * @param {Object} options - Options for transformation
 * @param {boolean} options.convertEmptyToNull - Whether to convert empty strings to null (default: true)
 * @param {boolean} options.excludeUiState - Whether to exclude UI state fields (default: false, set to true for exports)
 * @returns {Object} Transformed data compatible with regulator.js
 */
export const transformFormDataForRegulator = (formData, { convertEmptyToNull = true, excludeUiState = false } = {}) => 
  Object.fromEntries(
    Object.entries(formData)
      .filter(([key]) => !excludeUiState || !EXCLUDED_FROM_EXPORT_FIELDS.has(key))
      .map(([key, value]) => [key, transformValue(key, value, 'toRegulator', { convertEmptyToNull })]),
  )

/**
 * Transform checkbox string values ("checked") to boolean values (true) for React Hook Form
 * 
 * @param {Object} configData - The configuration data from regulator.js
 * @returns {Object} Transformed data with boolean checkbox values
 */
export const transformRegulatorDataForForm = (configData) => 
  Object.fromEntries(
    Object.entries(configData).map(([key, value]) => [key, transformValue(key, value, 'toForm')]),
  )

/**
 * Convert all values to strings for consistent JSON output (export/save)
 * This ensures that all configuration values are strings (except for nulls)
 * regardless of how they were entered or modified in the form
 * 
 * @param {Object} data - The data to convert
 * @returns {Object} Data with all values converted to strings (except nulls)
 */
export const convertValuesToStrings = (data) => {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      // Special handling for battery voltage fields - preserve non-empty values
      if ((key === 'batteryVoltage' || key === 'batteryVoltage2') && value && value !== '') {
        return [key, String(value)]
      }
      // Convert empty strings to 'null' string for save operations
      if (value === '') {
        return [key, 'null']
      }
      // Keep actual null/undefined as null (not string)
      if (value === null || value === undefined) {
        return [key, null]
      }
      // Convert all other values to strings
      return [key, String(value)]
    }),
  )
}

// Legacy exports for backward compatibility (direct access for better performance)
export const getCheckboxFields = () => fieldListsCache.checkboxFields
export const getExcludedFromExportFields = () => fieldListsCache.excludedFromExportFields

/**
 * Get field lists as arrays (memoized for performance)
 * @returns {Object} Object containing arrays of field names
 */
const fieldListsCache = Object.freeze({
  checkboxFields: Object.freeze([...CHECKBOX_FIELDS]),
  excludedFromExportFields: Object.freeze([...EXCLUDED_FROM_EXPORT_FIELDS]),
})

export const getFieldLists = () => fieldListsCache
